import { z } from 'zod';

export const LoanPartyRoleSchema = z.enum([
  'borrower1',
  'borrower2',
  'borrower3',
  'borrower4',
  'additional_borrower',
  'non_borrowing_spouse',
  'non_borrowing_individual',
]);
export type LoanPartyRole = z.infer<typeof LoanPartyRoleSchema>;

export const LockStatusNormalizedSchema = z.enum(['Locked', 'Not Locked', 'Unknown']);
export type LockStatusNormalized = z.infer<typeof LockStatusNormalizedSchema>;

export const LoanPartySchema = z.object({
  id: z.string(),
  loanId: z.string(),
  role: LoanPartyRoleSchema,
  fullName: z.string(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  sortOrder: z.number().int().nonnegative(),
});
export type LoanParty = z.infer<typeof LoanPartySchema>;

export const LoanDisclosureStatusSchema = z.object({
  loanId: z.string(),
  applicationOutDatetime: z.string().datetime().nullable().optional(),
  initialLoanEstimateDate: z.string().datetime().nullable().optional(),
  borr1LinkDelivered: z.string().datetime().nullable().optional(),
  borr1SignedAndSubmitted: z.string().datetime().nullable().optional(),
  borr2LinkDelivered: z.string().datetime().nullable().optional(),
  borr2SignedAndSubmitted: z.string().datetime().nullable().optional(),
  loSignedAndSubmitted: z.string().datetime().nullable().optional(),
  cdSignedFlag: z.enum(['Y', 'N']).nullable().optional(),
});
export type LoanDisclosureStatus = z.infer<typeof LoanDisclosureStatusSchema>;

export const LoanSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  lender: z.string().default('UWM'),
  loanNumber: z.string(),
  loanOfficer: z.string().nullable().optional(),
  loanOfficerEmail: z.string().email().nullable().optional(),
  loanOfficerNMLS: z.string().nullable().optional(),
  propertyAddress: z.string().nullable().optional(),
  propertyCity: z.string().nullable().optional(),
  propertyState: z.string().nullable().optional(),
  propertyZip: z.string().nullable().optional(),
  propertyCounty: z.string().nullable().optional(),
  loanStatus: z.string().nullable().optional(),
  loanStatusOrder: z.number().int().nullable().optional(),
  lockStatusRaw: z.string().nullable().optional(),
  lockStatus: LockStatusNormalizedSchema,
  lockExpired: z.enum(['Y', 'N']).nullable().optional(),
  loanPurpose: z.string().nullable().optional(),
  lockExpirationDate: z.string().datetime().nullable().optional(),
  outstandingCondition: z.number().int().nullable().optional(),
  mostRecentConditionDatetime: z.string().datetime().nullable().optional(),
  srUWFirstName: z.string().nullable().optional(),
  srUWLastName: z.string().nullable().optional(),
  srUWEmailAddress: z.string().email().nullable().optional(),
  srUWExt: z.string().nullable().optional(),
  uw2FirstName: z.string().nullable().optional(),
  uw2LastName: z.string().nullable().optional(),
  uw2EmailAddress: z.string().email().nullable().optional(),
  uw2Ext: z.string().nullable().optional(),
  lenderPortalUrl: z.string().url().nullable().optional(),
  titleCompanyEmail: z.string().email().nullable().optional(),
  assignedToContactId: z.string().nullable().optional(),
  uwmImportedDateTime: z.string().datetime().nullable().optional(),
  uwmRecordModifiedDateTime: z.string().datetime().nullable().optional(),
  dataHash: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Loan = z.infer<typeof LoanSchema>;

export const LoanListItemSchema = LoanSchema.extend({
  borrower1FullName: z.string().nullable().optional(),
  borrower2FullName: z.string().nullable().optional(),
  borrower3FullName: z.string().nullable().optional(),
  borrower4FullName: z.string().nullable().optional(),
  parties: z.array(LoanPartySchema).default([]),
  disclosures: LoanDisclosureStatusSchema.nullable().optional(),
});
export type LoanListItem = z.infer<typeof LoanListItemSchema>;

/**
 * Accept a query-string value that is either:
 *   - missing/empty → undefined
 *   - a single value: "Locked"
 *   - a CSV string:   "Locked,Not Locked"
 *   - already an array (when sent as `?key=a&key=b` Express parses to array)
 * Always normalizes to string[] (or undefined when empty).
 */
const csvOrArray = (inner: z.ZodTypeAny) =>
  z.preprocess((val) => {
    if (val === undefined || val === null || val === '') return undefined;
    if (Array.isArray(val)) {
      const flat = val.flatMap((v) => (typeof v === 'string' ? v.split(',') : [v]));
      const cleaned = flat.map((s) => String(s).trim()).filter(Boolean);
      return cleaned.length ? cleaned : undefined;
    }
    if (typeof val === 'string') {
      const parts = val.split(',').map((s) => s.trim()).filter(Boolean);
      return parts.length ? parts : undefined;
    }
    return val;
  }, z.array(inner).optional());

/**
 * "In Progress" status buckets for the My Pipeline tab. Matching is a
 * case-insensitive substring test because UWM prefixes most statuses with a
 * channel name (e.g. "Wholesale - File Imported", "Wholesale - Closing").
 *
 * Patterns chosen so they include exactly the four the business wants and
 * exclude look-alikes: "closing" matches "Wholesale - Closing" but NOT
 * "Post Closed" or "Post Close Problem"; "post close problem" is matched as a
 * full phrase so it does not pull in "Post Closed".
 */
export const IN_PROGRESS_STATUS_PATTERNS = [
  'file imported',
  'approved with conditions',
  'closing',
  'post close problem',
] as const;

/** True when a loan status belongs in the "In Progress" tab. */
export function isInProgressStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  const s = status.toLowerCase();
  return IN_PROGRESS_STATUS_PATTERNS.some((p) => s.includes(p));
}

/**
 * My Pipeline tab buckets:
 *   - in_progress: loanStatus matches one of IN_PROGRESS_STATUS_PATTERNS
 *   - other:       every other status (including null status)
 * Absence of the param means "no bucket constraint" (admin All-loans view).
 */
export const PipelineBucketSchema = z.enum(['in_progress', 'other']);
export type PipelineBucket = z.infer<typeof PipelineBucketSchema>;

export const PipelineFilterSchema = z.object({
  q: z.string().optional(),
  bucket: PipelineBucketSchema.optional(),
  loanStatus: csvOrArray(z.string()),
  lockStatus: csvOrArray(LockStatusNormalizedSchema),
  loanPurpose: csvOrArray(z.string()),
  assignedToContactId: csvOrArray(z.string()),
  outstandingConditionsGtZero: z.coerce.boolean().optional(),
  lockExpiringWithinDays: z.coerce.number().int().positive().optional(),
  missingInitialDisclosures: z.coerce.boolean().optional(),
  missingBorrowerSignature: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
  sortBy: z.enum(['lockExpirationDate', 'loanNumber', 'loanStatus', 'updatedAt']).default('lockExpirationDate'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
});
export type PipelineFilter = z.infer<typeof PipelineFilterSchema>;

export const AssignLoanInputSchema = z.object({
  assignedToContactId: z.string().nullable(),
  note: z.string().max(500).optional(),
});
export type AssignLoanInput = z.infer<typeof AssignLoanInputSchema>;

/**
 * Pin up to 2 extra org contacts to a loan's My Pipeline "Contacts" column
 * for quick click-to-email. The contacts must already exist in the org roster.
 */
export const UpdateLoanExtraContactsInputSchema = z.object({
  contactIds: z.array(z.string().min(1)).max(2),
});
export type UpdateLoanExtraContactsInput = z.infer<
  typeof UpdateLoanExtraContactsInputSchema
>;

/**
 * Admin-only manual-loan create input. Loan number is required + uniquely
 * identifies the row in combination with (organizationId, lender). Every
 * other field is optional and merge-friendly.
 */
export const CreateManualLoanInputSchema = z.object({
  loanNumber: z.string().min(1).max(64),
  lender: z.string().min(1).max(64).default('UWM'),
  loanOfficer: z.string().nullable().optional(),
  loanOfficerEmail: z.string().email().nullable().optional(),
  loanOfficerNMLS: z.string().nullable().optional(),
  propertyAddress: z.string().nullable().optional(),
  propertyCity: z.string().nullable().optional(),
  propertyState: z.string().nullable().optional(),
  propertyZip: z.string().nullable().optional(),
  loanStatus: z.string().nullable().optional(),
  loanStatusOrder: z.number().int().nullable().optional(),
  lockStatus: LockStatusNormalizedSchema.default('Unknown'),
  lockExpirationDate: z.string().datetime().nullable().optional(),
  loanPurpose: z.string().nullable().optional(),
  outstandingCondition: z.number().int().nullable().optional(),
  titleCompanyEmail: z.string().email().nullable().optional(),
  assignedToContactId: z.string().nullable().optional(),
  /**
   * Optional primary borrower full name. Stored as a LoanParty row with
   * role='borrower1' so the rest of the pipeline UI renders consistently.
   */
  borrower1FullName: z.string().nullable().optional(),
  borrower1Email: z.string().email().nullable().optional(),
  borrower1Phone: z.string().nullable().optional(),
});
export type CreateManualLoanInput = z.infer<typeof CreateManualLoanInputSchema>;

/** PATCH — every field optional except none required; merged into the row. */
export const UpdateLoanInputSchema = CreateManualLoanInputSchema.partial().omit({
  loanNumber: true,
  lender: true,
});
export type UpdateLoanInput = z.infer<typeof UpdateLoanInputSchema>;

export const UpdateTitleCompanyInputSchema = z.object({
  titleCompanyEmail: z.string().email().nullable(),
});
export type UpdateTitleCompanyInput = z.infer<typeof UpdateTitleCompanyInputSchema>;
